import { ReactNode, useEffect } from "react";

import { css } from "@compiled/react";
import { defineMessage, FormattedMessage } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import Form from "@atlaskit/form";
import { R400 } from "@atlaskit/theme/colors";

import {
    ufoEventPaidSignupJSWLandingPageLoad,
    ufoEventTryCloudSignupPageLoad,
    trackDataFoundationsAccountCreationComponent,
    fontSize
} from "@/components/signup/utils";
import { Link } from "@/libraries/design/components";
import { BgColors } from "@/libraries/design/components/link/src/link";
import { wacToken } from "@/libraries/design/foundations";

import {
    AnimatedSocialProof,
    ButtonSubmit,
    EulaText,
    ExistingProductText,
    InputEmail,
    SignInButtons,
    SignInProviders
} from "../components";
import { useSignupFormContext } from "../hooks";

const confluenceEmbeddedExistingText = defineMessage({
    id: "BxpUiSignup.SignupForm.existingProduct.confluence.embedded",
    defaultMessage: "Trying to access {product}? <logInLink>Log in</logInLink>"
});

export const PaidEmailForm = ({
    animatedSocialBanner,
    hasRebrandedSignupFormStyles,
    bgColor = "bgWhite",
    customSignInProviders,
    hideLegalText = false
}: {
    animatedSocialBanner?: boolean;
    hasRebrandedSignupFormStyles?: boolean;
    bgColor?: BgColors;
    customSignInProviders?: SignInProviders;
    hideLegalText?: boolean;
}) => {
    const {
        formError,
        hasSubmitted,
        isEmailValid,
        onSubmitWithReCAPTCHA,
        productKey,
        edition,
        setIsEmailValid,
        setTypeAheadEmail,
        productName
    } = useSignupFormContext();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    useEffect(() => {
        const customData = {
            webPlatform: "bxp-ui",
            reactComponent: "PaidEmailForm",
            experimentName: "paid-email-form",
            isLoggedIn: false
        };
        ufoEventTryCloudSignupPageLoad.success({
            customData
        });

        ufoEventPaidSignupJSWLandingPageLoad.success({
            customData: {
                reactComponent: "PaidEmailForm",
                experimentName: "paid-email-form"
            }
        });
        trackDataFoundationsAccountCreationComponent(createAnalyticsEvent, {
            actionSubjectId: "embedded",
            productKey,
            edition
        });
    }, [createAnalyticsEvent, productKey, edition]);

    return (
        <Form onSubmit={onSubmitWithReCAPTCHA} isDisabled={false}>
            {({ formProps, submitting }) => (
                <form {...formProps} autoComplete="off">
                    <InputEmail
                        setIsEmailValid={setIsEmailValid}
                        submitting={submitting}
                        setTypeAheadEmail={setTypeAheadEmail}
                        hasRebrandedSignupFormStyles={
                            hasRebrandedSignupFormStyles
                        }
                    />
                    {!hideLegalText && (
                        <p css={legalTextCss}>
                            <EulaText />
                        </p>
                    )}
                    <ButtonSubmit
                        data-testid="signup-form-submit"
                        submitting={submitting}
                        hasSubmitted={hasSubmitted}
                        isEmailValid={isEmailValid}
                        formError={false}
                        hasRebrandedSignupFormStyles={
                            hasRebrandedSignupFormStyles
                        }
                    />
                    {formError && (
                        <p className="errorMessageText" css={formErrorStyle}>
                            <FormattedMessage
                                id="BxpUiSignup.SignupForm.formError"
                                defaultMessage="It's taking longer than expected to get you started. Please check your inbox to see if the order has completed successfully, or <contactUsLink>contact us</contactUsLink>."
                                values={{
                                    contactUsLink: (text: ReactNode) => (
                                        <Link
                                            text={text as unknown as string}
                                            aria-label={
                                                text as unknown as string
                                            }
                                            type="inline"
                                            href="/company/contact"
                                            className="linkText"
                                        />
                                    )
                                }}
                            />
                        </p>
                    )}
                    <div css={separatorContainer}>
                        <div css={orPaidSeparatorStyle} />
                        <span className="separatorText" css={separatorText}>
                            <FormattedMessage
                                id="BxpUiSignup.SignupForm.orPaidExperiment"
                                defaultMessage="Or sign up with"
                            />
                        </span>
                        <div css={orPaidSeparatorStyle} />
                    </div>
                    <SignInButtons
                        hasRebrandedSignupFormStyles={
                            hasRebrandedSignupFormStyles
                        }
                        customSignInProviders={customSignInProviders}
                    />
                    {animatedSocialBanner && (
                        <div css={animatedSocialProof}>
                            <AnimatedSocialProof />
                        </div>
                    )}
                    <ExistingProductText
                        messageOverride={confluenceEmbeddedExistingText}
                        productName={productName}
                        bgColor={bgColor}
                    />
                </form>
            )}
        </Form>
    );
};

const animatedSocialProof = css({
    marginBlockStart: "40px"
});

const formErrorStyle = css({
    color: `${R400} !important`,
    margin: "6px 0 24px"
});

const legalTextCss = css({
    fontSize: fontSize.xs,
    lineHeight: 1.5
});

const orPaidSeparatorStyle = css({
    borderBlockEnd: `.25px solid #B3BAC5`,
    lineHeight: ".25px",
    margin: "2rem 1rem",
    flexGrow: "1"
});
const separatorText = css({
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "var(--dark-text-warning-inverse, #000)",
    fontFamily: wacToken("font.charlie.text"),
    fontWeight: "400"
});
const separatorContainer = css({
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center"
});
